// Application domain types

// Values must match backend API exactly
// Const object replaces enum — compatible with erasableSyntaxOnly
export const ApplicationStatus = {
  NOT_STARTED: 'TO_START',
  IN_PROGRESS: 'PROCESS',
  SENT: 'SENT',
  WAITING_RESPONSE: 'WAITING',
  NOT_SELECTED: 'REJECTED',
} as const;

export type ApplicationStatus = (typeof ApplicationStatus)[keyof typeof ApplicationStatus];

// Core application entity - matches GET /api/v1/applications response
export interface Application {
  id: string;
  companyId: string;
  contestId: string | null;
  name: string;
  url: string;
  country?: string;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  status: ApplicationStatus;
  progress?: number; // 0-100, calculated from status or user-set
  createdAt: string;
  lastEditedAt?: string | null;
  lastEditedBy?: string | null; // display name of last editor
}

// Create application payload - POST /api/v1/applications
export interface CreateApplicationPayload {
  companyId: string;
  name: string;
  url: string;
  startDate: string;
  endDate: string;
  status?: ApplicationStatus;
  country?: string;
  progress?: number;
}

// Update application payload - PATCH /api/v1/applications/:id
export interface UpdateApplicationPayload {
  name?: string;
  url?: string;
  startDate?: string;
  endDate?: string;
  status?: ApplicationStatus;
  country?: string;
  progress?: number;
}

// Pagination metadata from API response
export interface PaginatedMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

// Generic paginated response wrapper
export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginatedMeta;
}

// Query params for fetching applications with pagination/filters/sort
export interface ApplicationQueryParams {
  page?: number;
  limit?: number;
  companyId?: string;
  status?: ApplicationStatus;
  search?: string;
  sortBy?: 'endDate' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Response from GET /api/v1/applications/count
export interface ApplicationCountResponse {
  total: number;
  byStatus: Record<ApplicationStatus, number>;
}

// Legacy types kept for backwards compatibility
export interface ApplicationsResponse {
  applications: Application[];
  total: number;
}

export interface ApplicationResponse {
  application: Application;
}
